import * as React from 'react';
import * as moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Card, CardActions, CardContent, styled, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import HappinessScore from './happinessScore';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import IHappinessRating from '../../../../common/models/IHappinessRating';
import Textarea from '../../components/text-area/textarea';

interface HappinessDayNoteProps {
	belongsToUser: boolean,
	ratingInfo: IHappinessRating,
	dayColor: string | undefined,
	dayScore: number | undefined,
	dayComment: string,
	canEdit: boolean,
	canDelete: boolean,
	dayIcon: string,
	isConnected: boolean,
	isBusy: boolean,
	displayedDate: moment.Moment,
	handleSave: () => void,
	handleDelete: () => void,
	handleCommentChange: (comment: string) => void,
	handleScoreChange: (score: number) => void
}

function HappinessDayNote(props: HappinessDayNoteProps) {

	const {
		belongsToUser, displayedDate, dayColor, dayComment, dayScore, dayIcon, canEdit, canDelete,
		isBusy, isConnected, ratingInfo, handleDelete, handleSave, handleCommentChange, handleScoreChange} = props;

	const [localDayComment, setLocalDayComment] = useState(dayComment);

	const [showOverlay, setShowOverlay] = useState(belongsToUser && !ratingInfo && !ratingInfo?.comment);
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const scoreDefaultValue = 4;
	useEffect(() => {
		const shouldShowOverlay = belongsToUser && !ratingInfo && !ratingInfo?.comment;
		setShowOverlay(shouldShowOverlay);
	}, [belongsToUser, ratingInfo]);

	useEffect(() => {
		// Focus the textarea when the overlay is not shown
		if (!showOverlay && textAreaRef.current) {
			textAreaRef.current.focus();
		}
	}, [showOverlay]);

	useEffect(() => {
		// Update local state when the dayComment prop changes
		setLocalDayComment(dayComment);
	}, [dayComment]);

	// Handler to pass to the TextareaAutosize component
	const handleTextareaChange = useCallback((event) => {
		setLocalDayComment(event.target.value); // Update local state for immediate feedback
	}, [setLocalDayComment]);

	const handleAddNewEntryClick = useCallback(() => {
		setShowOverlay(false);
	}, [setShowOverlay]);

	const handleOnSave = useCallback(() => {
		if(!dayScore) {
			handleScoreChange(scoreDefaultValue);
		}
		handleCommentChange(localDayComment);
		handleSave();
	}, [dayScore, localDayComment, handleScoreChange, handleCommentChange, handleSave]);

	return (
		<div key={`day-note-card-${displayedDate}`} className='calendarDayNoteCard'>
			<Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
				<Avatar sx={{ bgcolor: 'transparent', fontSize: '1.5em' }}>{ dayIcon ?? '🩷' }</Avatar>
				<Typography variant='h6' color='secondary'>{displayedDate.format('dddd MMMM Do YYYY')}</Typography>
			</Box>
			<Card sx={{ position: 'relative', width: '100%', backgroundColor: dayColor }} variant='outlined'>
				{showOverlay &&
        <Box className='dateNoteOverlay'>
            <Typography variant='body1' color='secondary' sx={{ textAlign: 'center' }}>
		            Your journal entry for this day is a blank canvas <br/> Time to fill it in? 🫣
						</Typography>
            <br/>
            <Button size='small' onClick={handleAddNewEntryClick} color='secondary' variant='contained'>
                Add new entry
            </Button>
        </Box>
				}
				<CardContent>
					{canEdit ?
						<div className='happinessReflection'>
							<Typography variant='body1' marginBottom='3px'>
								🌿 Reflect on Your Day
							</Typography>
							<Typography variant='caption' marginBottom='10px' paragraph>
								Did anything make your day special or challenging?
							</Typography>
							<Textarea minRows={1}
							          maxRows={5}
							          ref={textAreaRef}
							          onChange={handleTextareaChange}
							          value={localDayComment} />
							<HappinessScore handleScoreChange={handleScoreChange} value={dayScore} defaultValue={scoreDefaultValue}/>
						</div> :
						<>
							{ratingInfo?.score === undefined ?
								<div style={{ display: 'flex', justifyContent: 'center' }}>
									<Typography variant='body1' color='secondary' sx={{ textAlign: 'center' }}>
										There is no daily reflection for this day 🙁
									</Typography>
								</div>
								:
								<>
									<Typography variant='body1' marginBottom={'10px'}>
										🌿 Day Reflection
									</Typography>
									<Typography variant='body2'>
										{ratingInfo && ratingInfo.comment}
									</Typography>
								</>
							}
						</>
					}
				</CardContent>
        <CardActions sx={{ padding: '0 16px 16px 16px' }}>
	        { !isBusy && canDelete ?
						<Button size='small'
						        color='secondary'
						        variant='outlined'
					          onClick={handleDelete}
					          startIcon={<DeleteIcon />}
						>
							Delete
					</Button> : undefined }
					{ !isBusy && canEdit ?
						<Button size='small'
					          onClick={handleOnSave}
					          disabled={!isConnected || !localDayComment}
					          startIcon={<FavoriteIcon />}
					          color='error'
					          variant='outlined'
						>
							Save
						</Button> : isBusy ?
							<LoadingButton
								loading
								variant='outlined'
								sx={{ height: '30px' }}
							/> : null }
        </CardActions>
			</Card>
		</div>
	);
}

export default React.memo(HappinessDayNote);
